import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  #abortController = new AbortController();
  /** @type {'touch' | 'mouse' | 'pen' | 'keyboard' | null} */
  #lastInteractionType = null;

  static DESKTOP_MEDIA_QUERY = '(min-width: 990px)';
  static HOVER_MEDIA_QUERY = '(hover: hover) and (pointer: fine)';

  /**
   * @typedef {Object} ChildDropdownState
   * @property {HTMLUListElement} ul
    * @property {Element | null} originalParent
    * @property {ChildNode | null} originalNextSibling
   * @property {number | null} closeTimeoutId
    * @property {boolean} listenersBound
   */

  /** @type {Map<HTMLElement, ChildDropdownState>} */
  #childDropdowns = new Map();

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);

    onDocumentLoaded(this.#setupChildDropdownPointerMenus);

    this.addEventListener('pointerdown', this.#handlePointerDown, { signal: this.#abortController.signal });
    this.addEventListener('keydown', this.#handleKeyDown, { signal: this.#abortController.signal });
    this.addEventListener('click', this.#handleMenuClick, { signal: this.#abortController.signal });
    document.addEventListener('click', this.#handleDocumentClick, { signal: this.#abortController.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#restoreChildDropdownPointerMenus();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  #canUseHoverMenus() {
    return window.matchMedia(HeaderMenu.HOVER_MEDIA_QUERY).matches;
  }

  /**
   * @param {Event} event
   * @returns {boolean}
   */
  #shouldIgnorePointerHoverEvent(event) {
    return event instanceof PointerEvent && (event.pointerType === 'touch' || !this.#canUseHoverMenus());
  }

  /**
   * @param {Event} event
   * @returns {boolean}
   */
  #shouldIgnoreTouchFocusEvent(event) {
    return event instanceof FocusEvent && this.#lastInteractionType === 'touch';
  }

  #shouldUseTapToToggle() {
    return this.#lastInteractionType === 'touch' || !this.#canUseHoverMenus();
  }

  #isDesktopMenuViewport() {
    return window.matchMedia(HeaderMenu.DESKTOP_MEDIA_QUERY).matches;
  }

  /**
   * @param {Node} target
   * @returns {boolean}
   */
  #isInsideChildDropdown(target) {
    if (!(target instanceof Element)) return false;

    for (const state of this.#childDropdowns.values()) {
      if (state?.ul?.contains(target)) {
        return true;
      }
    }

    return false;
  }

  #closeChildDropdowns() {
    for (const state of this.#childDropdowns.values()) {
      if (!state?.ul) continue;
      state.ul.removeAttribute('data-open');

      if (state.closeTimeoutId != null) {
        clearTimeout(state.closeTimeoutId);
        state.closeTimeoutId = null;
      }
    }
  }

  /** @param {HTMLElement} link */
  #getChildDropdownState(link) {
    let state = this.#childDropdowns.get(link);
    if (state) return state;

    const ul = link.nextElementSibling;
    if (!(ul instanceof HTMLUListElement)) return null;

    ul.classList.add('mega-menu__child-dropdown');

    state = {
      ul,
      originalParent: ul.parentElement,
      originalNextSibling: ul.nextSibling,
      closeTimeoutId: null,
      listenersBound: false,
    };

    if (!state.listenersBound) {
      state.listenersBound = true;
      ul.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch') return;
        this.#clearChildDropdownClose(state);
        this.#debouncedDeactivate.cancel();
      }, { signal: this.#abortController.signal });

      ul.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'touch') return;
        this.#scheduleChildDropdownClose(state);
        this.#debouncedDeactivate();
      }, { signal: this.#abortController.signal });
    }

    this.#childDropdowns.set(link, state);
    return state;
  }

  /** @param {ChildDropdownState | null} state */
  #clearChildDropdownClose(state) {
    if (state?.closeTimeoutId != null) {
      clearTimeout(state.closeTimeoutId);
      state.closeTimeoutId = null;
    }
  }

  /** @param {ChildDropdownState | null} state */
  #scheduleChildDropdownClose(state) {
    if (!state) return;

    this.#clearChildDropdownClose(state);
    state.closeTimeoutId = window.setTimeout(() => {
      state.ul.removeAttribute('data-open');
      state.closeTimeoutId = null;
    }, 120);
  }

  /**
   * @param {{ restore?: boolean }} [options]
   */
  #hideChildDropdowns(options = {}) {
    const { restore = false } = options;

    for (const [link, state] of this.#childDropdowns.entries()) {
      if (!state?.ul) continue;

      state.ul.removeAttribute('data-open');
      state.ul.style.position = '';
      state.ul.style.left = '';
      state.ul.style.top = '';
      state.ul.style.zIndex = '';
      state.ul.style.maxHeight = '';
      state.ul.style.overflowY = '';

      if (restore && state.ul.parentElement === document.body) {
        if (state.originalParent instanceof Element) {
          state.originalParent.insertBefore(state.ul, state.originalNextSibling);
        } else if (link.isConnected) {
          link.after(state.ul);
        }
      }

      this.#clearChildDropdownClose(state);
    }
  }

  /** @param {HTMLUListElement} ul @param {HTMLElement} link */
  #positionChildDropdownForLink(ul, link) {
    const linkRect = link.getBoundingClientRect();
    const gapX = 8;
    const offsetY = 0;
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = document.documentElement.clientHeight;

    /** @param {number} value @param {number} min @param {number} max */
    const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

    ul.style.position = 'fixed';
    ul.style.left = `${Math.round(linkRect.right + gapX)}px`;
    ul.style.top = `${Math.round(linkRect.top + offsetY)}px`;
    ul.style.zIndex = '9999';

    const rect = ul.getBoundingClientRect();
    let nextLeft = rect.left;

    if (rect.right > viewportWidth - 8) {
      nextLeft = linkRect.left - rect.width - gapX;
    }

    nextLeft = clamp(nextLeft, 8, Math.max(8, viewportWidth - rect.width - 8));
    const nextTop = clamp(rect.top, 8, Math.max(8, viewportHeight - rect.height - 8));

    ul.style.left = `${Math.round(nextLeft)}px`;
    ul.style.top = `${Math.round(nextTop)}px`;
    ul.style.maxHeight = `${Math.max(120, viewportHeight - nextTop - 8)}px`;
    ul.style.overflowY = 'auto';
  }

  /** @param {HTMLElement} link */
  #openChildDropdown(link) {
    if (!this.#isDesktopMenuViewport()) return null;

    const state = this.#getChildDropdownState(link);
    if (!state) return null;

    this.#clearChildDropdownClose(state);
    this.#hideChildDropdowns();

    const { ul } = state;
    ul.classList.add('mega-menu__child-dropdown');

    if (ul.parentElement !== document.body) {
      document.body.appendChild(ul);
    }

    this.#positionChildDropdownForLink(ul, link);
    ul.dataset.open = 'true';

    return state;
  }

  /**
   * @param {HTMLElement | null} item
   */
  #activateItem(item) {
    if (!item || item == this.#state.activeItem) return;

    this.#closeChildDropdowns();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    this.dataset.overflowExpanded = 'false';

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    const submenu = findSubmenu(item);

    if (submenu) {
      submenu.dataset.active = '';
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  }

  /**
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    this.#lastInteractionType = ['touch', 'mouse', 'pen'].includes(event.pointerType)
      ? /** @type {'touch' | 'mouse' | 'pen'} */ (event.pointerType)
      : null;
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key === 'Tab') {
      this.#lastInteractionType = 'keyboard';
    }
  };

  /**
   * @param {MouseEvent} event
   */
  #handleMenuClick = (event) => {
    if (!this.#shouldUseTapToToggle()) return;
    if (!(event.target instanceof Element)) return;

    const childParentLink = event.target.closest('.mega-menu__link--parent');

    if (childParentLink instanceof HTMLAnchorElement && this.contains(childParentLink)) {
      const state = this.#getChildDropdownState(childParentLink);

      if (state) {
        if (state.ul.dataset.open === 'true') {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        this.#debouncedDeactivate.cancel();
        this.#debouncedActivateHandler.cancel();
        this.#openChildDropdown(childParentLink);
        childParentLink.focus();
        return;
      }
    }

    const link = event.target.closest('.menu-list__link');

    if (!(link instanceof HTMLAnchorElement) || !this.contains(link)) return;

    const item = findMenuItem(link);
    const submenu = findSubmenu(item);

    if (!item || !submenu) return;

    if (this.#state.activeItem === item) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();
    this.#activateItem(item);
    link.focus();
  };

  /**
   * @param {MouseEvent} event
   */
  #handleDocumentClick = (event) => {
    if (!this.#state.activeItem || !this.#shouldUseTapToToggle()) return;
    if (!(event.target instanceof Node)) return;
    if (this.contains(event.target)) return;
    if (this.#isInsideChildDropdown(event.target)) return;

    this.#deactivate();
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (this.#shouldIgnorePointerHoverEvent(event) || this.#shouldIgnoreTouchFocusEvent(event)) return;

    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    this.#activateItem(item);
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (this.#shouldIgnorePointerHoverEvent(event) || this.#shouldIgnoreTouchFocusEvent(event)) return;

    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    this.#closeChildDropdowns();

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
      // Remove active state from submenu after animation completes
      if (submenu) {
        delete submenu.dataset.active;
      }
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Desktop-only: show the child dropdown (".mega-menu__link--parent + ul") as a side flyout.
   * We portal the UL into <body> to avoid clipping, and position it next to the hovered link.
   */
  #setupChildDropdownPointerMenus = () => {
    const mediaQuery = window.matchMedia(`${HeaderMenu.DESKTOP_MEDIA_QUERY} and ${HeaderMenu.HOVER_MEDIA_QUERY}`);

    const links = Array.from(this.querySelectorAll('.mega-menu__link--parent'))
      .filter((el) => el instanceof HTMLElement);

    if (!links.length) return;

    for (const link of links) {
      const ul = link.nextElementSibling;
      if (!ul) continue;

      this.#getChildDropdownState(link);

      link.addEventListener('pointerenter', (event) => {
        if (!mediaQuery.matches || event.pointerType === 'touch') return;
        this.#openChildDropdown(link);
      }, { signal: this.#abortController.signal });

      link.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'touch') return;
        this.#scheduleChildDropdownClose(this.#getChildDropdownState(link));
      }, { signal: this.#abortController.signal });
    }

    mediaQuery.addEventListener('change', () => {
      if (mediaQuery.matches) return;
      this.#hideChildDropdowns({ restore: true });
    }, { signal: this.#abortController.signal });
  };

  #restoreChildDropdownPointerMenus = () => {
    this.#hideChildDropdowns({ restore: true });
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element.matches('[ref="menuitem"]')) {
    return /** @type {HTMLElement} */ (element);
  }

  const listItem = element.closest('.menu-list__list-item') || element;
  return /** @type {HTMLElement | null} */ (listItem.querySelector('[ref="menuitem"]'));
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
